import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Chart,
  PolarAreaController,
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { PolarArea } from 'react-chartjs-2';
Chart.register(PolarAreaController, RadialLinearScale, ArcElement, Tooltip, Legend);


interface PolarAreaChartProps {
  data?: number[];
  labels?: string[];
}

const PolarAreaChart: React.FC<PolarAreaChartProps> = ({ data: propData, labels: propLabels, ...props }) => {


  const labels = propLabels || ['Long Lupron', 'Short Lupron', 'Antagonist', 'Minimal', 'Natural', 'Others'];
  const values = propData || [67.2, 55.1, 48.3, 62.0, 35.5, 22.4];

  // Generate color shades based on value (higher = darker)
  function getShade(val: number, min: number, max: number) {
    // interpolate between 63 (dark) and 240 (light) for R/G/B
    const shade = Math.round(240 - ((val - min) / (max - min || 1)) * (240 - 63));
    return `rgb(${shade},${shade+10},${shade+20})`;
  }
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const backgroundColor = values.map(v => getShade(v, minVal, maxVal));

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor,
        borderWidth: 0,
      },
    ],
  };

  // Plugin to draw concentric rings ticks labels (10,20,...100)
  const ringsPlugin: Plugin<'polarArea'> = {
    id: 'ringsPlugin',
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const scale = chart.scales['r'];
      if (!scale) return;
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.06)';
      ctx.lineWidth = 1;
      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
      const steps = 10;
      for (let i = 1; i <= steps; i++) {
        const r = (Math.min(chart.chartArea.right - chart.chartArea.left, chart.chartArea.bottom - chart.chartArea.top) / 2) * (i / steps);
        ctx.beginPath();
        ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      // draw percent labels on radial axis at top
      ctx.fillStyle = '#9E9E9E';
      ctx.font = '12px Montserrat';
      for (let i = 0; i <= 10; i++) {
        const val = i * 10;
        const r = (Math.min(chart.chartArea.right - chart.chartArea.left, chart.chartArea.bottom - chart.chartArea.top) / 2) * (i / 10);
        ctx.fillText(String(val), centerX + 4, centerY - r - 4);
      }
      ctx.restore();
    },
  };

  const options: ChartOptions<'polarArea'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }, // use custom MUI legend for exact spacing
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx) => {
            const label = ctx.label ?? '';
            const val = Number(ctx.raw ?? 0);
            return `${val.toFixed(1)}%\n(${label})`;
          },
        },
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: { display: false },
        grid: { circular: true, color: 'rgba(0,0,0,0.04)' },
      },
    },
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }} {...props}>
      
      {/* custom legend */}
      <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={3}>
          {labels.map((lbl, i) => (
            <Box key={lbl} display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: data.datasets[0].backgroundColor[i] }} />
              <Typography variant="body2" sx={{ color: '#6B6B6B' }}>{lbl}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box display="flex" justifyContent="center">
        <Box sx={{ width: { xs: '60%', md: '44%' }, height: { xs: 360, md: 440 }, position: 'relative' }}>
          <PolarArea data={data} options={options} plugins={[ringsPlugin]} />
        </Box>
      </Box>
    </Paper>
  );
};

export default PolarAreaChart;
